import React from 'react';
import { TextField, styled } from '@mui/material';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import { translate } from '../../helper';

const StyledInput = styled(TextField)(({ theme, style }) => {
    const isCompact44 = style && String(style.height) === '44';
    const convertToPx = (v) => (typeof v === 'number' ? `${v}px` : v);
    const backgroundColor = style?.backgroundColor ?? (isCompact44 ? '#fff' : '#F8F8F8');
    const borderRadius = style?.borderRadius ?? 'calc(var(--sp-xxs)*1.88679)';
    const boxShadow = style?.boxShadow;
    const inputFontSize = style?.inputFontSize ? convertToPx(style.inputFontSize) : 'calc(var(--fs-xs)*1.6)';
    const inputFontWeight = style?.inputFontWeight ?? undefined;
    const inputTextColor = style?.color ?? undefined;
    const noBorder = Boolean(style?.noBorder);
    const outlineColor = style?.outlineColor ?? '#C8C8C8';

    return {
        '& .MuiOutlinedInput-root': {
            borderRadius,
            backgroundColor,
            boxShadow,
            padding: '0px',
            minHeight: 'calc(var(--fs-hero)*1.157894)',
            height: 'calc(var(--fs-hero)*1.157894)',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            '& fieldset': { top: 0 },
            '& :disabled': {
                color: 'rgba(0, 0, 0, 0.21)'
            },
            'fontSize': 'calc(var(--fs-xs)*1.6)',
            '&.Mui-focused fieldset': {
                borderColor: '#1976d2 !important'
            }
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: outlineColor,
            border: noBorder ? 'none' : undefined,
        },
        '& .MuiFormHelperText-root': {
            marginLeft: '0px',
            borderRadius: 'inherit',
            fontSize:'var(--fs-sm)'
        },
        '& input': {
            padding: isCompact44 ? '0 calc(var(--sp-md2)*1.13207)' : 'calc(var(--sp-xxs)*2) calc(var(--sp-md2)*1)',
            height: 'calc(var(--fs-hero)*1.157894)',
            lineHeight: isCompact44 ? 'calc(var(--fs-hero)*1.0377)' : 'normal',
            fontSize: inputFontSize,
            fontWeight: inputFontWeight,
            color: inputTextColor,
        }
    };
});

const StyledTextArea = styled(TextareaAutosize)(({ theme }) => ({
    'boxSizing': 'border-box',
    'background': '#F8F8F8',
    'width': '100%',
    'padding': 'calc(var(--sp-xxs)*2) calc(var(--sp-md2)*1.13207)',
    'borderRadius': '8px',
    'borderColor': '#C8C8C8'
}));

export const Label = styled('label')(({ theme }) => ({
    'fontSize': 'calc(var(--fs-xs)*1.6)',
    'fontWeight': '400',
    'marginBottom': 'calc(var(--sp-xxs)*2)',
    'display': 'block',
    'color': '#000',
    'lineHeight': 'normal',
    'maxWidth': 'calc(var(--fs-hero)*6.75)',
    'flex': '0 0 calc(var(--fs-hero)*6.75)',
}));

export default function TextInput({
    label,
    placeholder,
    value,
    onChange,
    id,
    name,
    onBlur,
    onFocus,
    onClick,
    error,
    type,
    asTextArea,
    rows,
    disabled,
    className,
    InputProps,
    fullWidth,
    helperText,
    minRows,
    multiline,
    style={},
    labelColor,
    labelFontSize,
    labelFontWeight,
    ...rest
}) {
    const MainComponent = asTextArea ? StyledTextArea : StyledInput;
    const convertToPx = (v) => (typeof v === 'number' ? `${v}px` : v);
    return (
        <>
            {label && (
                <Label htmlFor={id} className='field-label' style={{ color: labelColor, fontSize: labelFontSize ? convertToPx(labelFontSize) : undefined, fontWeight: labelFontWeight }}>{label}</Label>
            )}
            <MainComponent
                className={className}
                data-testid={rest['data-testid']}
                placeholder={translate(placeholder)}
                value={value}
                onChange={onChange}
                id={id}
                name={name}
                onBlur={onBlur}
                onFocus={onFocus}
                onClick={onClick}
                error={error}
                type={type}
                disabled={disabled}
                InputProps={InputProps}
                fullWidth={fullWidth}
                helperText={translate(helperText)}
                rows={rows}
                minRows={minRows}
                multiline={multiline}
                style={style}
            />
        </>
    );
}
